<template>
  <div class="scanner-container">
    <video ref="videoRef" class="scanner-video" autoplay playsinline></video>
    <div class="scanner-overlay">
      <div class="scan-frame">
        <div class="scan-line"></div>
      </div>
      <div class="hint-text">将二维码放入框内，即可自动扫描</div>
    </div>

    <!-- 手机端日志展示（可折叠） -->
    <!-- <div class="log-panel" @click="toggleLog">
      <div class="log-title">📱 扫码日志 (点击展开/收起)</div>
      <div class="log-content" v-show="showLog">
        <div v-for="(log, index) in logList" :key="index" :class="['log-item', log.type]">
          <span class="log-time">{{ log.time }}</span>
          <span class="log-text">{{ log.text }}</span>
        </div>
      </div>
    </div> -->
    <!-- <div class="quality-display" v-if="lastScanResult">
      <div class="result-label">最新扫描结果：</div>
      <div class="result-content">{{ JSON.parse(lastScanResult).content }}</div>
      <div class="debug-info" v-if="parsedDebugInfo">
        <div class="debug-title">调试信息：</div>
        <div class="debug-item">画布尺寸：{{ parsedDebugInfo.canvasSize }}</div>
        <div class="debug-item">扫描框位置：{{ parsedDebugInfo.framePosition }}</div>
        <div class="debug-item">扫描框大小：{{ parsedDebugInfo.frameSize }}</div>
        <div class="debug-item">视频尺寸：{{ parsedDebugInfo.videoSize }}</div>
      </div>
    </div> -->
    <!-- <div class="quality-display" v-if="currentQuality">
      <div class="quality-title">📊 当前图像质量 (每3秒更新)</div>
      <div class="quality-content">
        <div class="quality-item">亮度范围: {{ currentQuality.range }}</div>
        <div class="quality-item">平均亮度: {{ currentQuality.avg }}</div>
        <div class="quality-item">对比度: {{ currentQuality.contrast }}</div>
        <div class="quality-item">黑白像素比: {{ currentQuality.darkRatio }}</div>
        <div class="quality-item">边缘强度: {{ currentQuality.edgeStrength }}</div>
      </div>
      <div class="quality-warning" v-if="currentQuality.warning">
        ⚠️ {{ currentQuality.warning }}
      </div>
    </div> -->
    <div class="control-buttons">
      <input type="file" ref="fileInput" accept="image/*" @change="handleFileChange" style="display: none;" />
      <van-button type="primary" size="large" @click="triggerFileUpload">上传图片</van-button>
      <van-button type="default" size="large" @click="handleClose" style="margin-top: 20px;">取消扫描</van-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { showToast } from 'vant'
import jsQR from 'jsqr'

const emit = defineEmits<{
  (e: 'scan-success', value: string): void
  (e: 'scan-close'): void
}>()
const parsedDebugInfo = computed(() => {
  try {
    const data = JSON.parse(lastScanResult.value)
    return data.debug
  } catch {
    return null
  }
})
// 响应式数据
const videoRef = ref<HTMLVideoElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const ctxRef = ref<CanvasRenderingContext2D | null>(null)
const isScanning = ref(false)
let scanTimer: number | null = null

// 日志相关
const logList = ref<Array<{ time: string, text: string, type: 'info' | 'error' | 'warn' | 'success' }>>([])
const showLog = ref(false)

// 日志记录方法
const addLog = (text: string, type: 'info' | 'error' | 'warn' | 'success' = 'info') => {
  const time = new Date().toLocaleTimeString()
  const logItem = { time, text, type }
  logList.value.push(logItem)
  // 只保留最近50条日志，避免内存溢出
  if (logList.value.length > 50) {
    logList.value.shift()
  }
}

// 切换日志显示/隐藏
const toggleLog = () => {
  showLog.value = !showLog.value
}

// 初始化摄像头 + 二维码循环识别
const initQRScanner = async () => {
  try {
    addLog('开始初始化扫码器')

    // 1. 检查浏览器兼容性
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('当前浏览器不支持摄像头API')
    }
    addLog('浏览器支持摄像头API', 'success')

    // 2. 请求摄像头权限
    addLog('请求摄像头权限（后置摄像头）')
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { 
        facingMode: { ideal: 'environment' }, // 强制后置摄像头
        // width: { ideal: 640, min: 320 }, 
        // height: { ideal: 640, min: 320 }
      }
    })
    addLog('摄像头权限获取成功，流信息：' + stream.id, 'success')

    // 3. 绑定视频流
    if (!videoRef.value) {
      throw new Error('视频元素未找到')
    }
    videoRef.value.srcObject = stream
    addLog('视频流绑定成功，准备播放')

    // 4. 播放视频
    await videoRef.value.play()
    addLog('视频播放成功，分辨率：' + videoRef.value.videoWidth + 'x' + videoRef.value.videoHeight, 'success')

    // 5. 创建canvas用于识别
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Canvas上下文创建失败')
    }
    canvasRef.value = canvas
    ctxRef.value = ctx
    addLog('Canvas初始化成功', 'success')

    // 6. 开始扫描循环
    isScanning.value = true
    addLog('开始二维码识别循环', 'success')
    startScanLoop()

  } catch (err: any) {
    addLog('初始化失败：' + (err.message || err.toString()), 'error')
    // showToast({
    //   type: 'fail',
    //   message: '扫码初始化失败：' + (err.message || '未知错误'),
    //   duration: 3000
    // })
  }
}

// 循环识别（节流，每秒最多10次）
const SCAN_INTERVAL = 100; // ms
const startScanLoop = () => {
  if (!isScanning.value) return;
  scanTimer = window.setTimeout(() => {
    try {
      scan();
    } catch (scanErr: any) {
      addLog('单帧识别出错：' + scanErr.message, 'warn');
    }
    startScanLoop();
  }, SCAN_INTERVAL);
}
const currentQuality = ref<{
  range: string,
  avg: string,
  contrast: number,
  darkRatio: string,
  edgeStrength: string,
  warning?: string
} | null>(null)
const lastScanResult = ref('')
// 扫描失败计数器
let scanFailCount = 0;
const SCAN_FAIL_THRESHOLD = 8; // 连续失败8次后全屏扫描

// 识别单帧
const scan = () => {
  const video = videoRef.value;
  const canvas = canvasRef.value;
  const ctx = ctxRef.value;

  if (!video || !canvas || !ctx) {
    addLog('识别失败：视频/画布元素缺失', 'warn');
    return;
  }

  if (video.readyState !== 4) {
    return;
  }

  // 固定canvas分辨率为640x480，提升识别性能
  const canvasWidth = 640;
  const canvasHeight = 480;
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  try {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // 将视频帧等比缩放绘制到canvas
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    if (!videoWidth || !videoHeight) return;
    // 计算缩放比例，保持画面不变形，居中显示
    const scale = Math.min(canvasWidth / videoWidth, canvasHeight / videoHeight);
    const drawW = videoWidth * scale;
    const drawH = videoHeight * scale;
    const offsetX = (canvasWidth - drawW) / 2;
    const offsetY = (canvasHeight - drawH) / 2;
    ctx.drawImage(video, 0, 0, videoWidth, videoHeight, offsetX, offsetY, drawW, drawH);

    // 扫描框尺寸
    const frameSize = 300;
    // 计算扫描框位置
    let frameX = (canvas.width - frameSize) / 2;
    let frameY = (canvas.height - frameSize) / 2;
    frameX = Math.max(0, Math.min(frameX, canvas.width - frameSize));
    frameY = Math.max(0, Math.min(frameY, canvas.height - frameSize));

    let code = null;
    let usedFullScreen = false;

    // 优化A：连续失败N次后自动全屏扫描
    if (scanFailCount >= SCAN_FAIL_THRESHOLD || canvas.width < frameSize || canvas.height < frameSize) {
      addLog(`连续失败${scanFailCount}次，或画布小于扫描框，使用全屏扫描`, 'warn');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      code = jsQR(imageData.data, canvas.width, canvas.height, {
        inversionAttempts: 'dontInvert'
      });
      usedFullScreen = true;
    } else {
      addLog(`扫描框位置: (${frameX.toFixed(0)}, ${frameY.toFixed(0)}) 尺寸:${frameSize}x${frameSize}`, 'info');
      const imageData = ctx.getImageData(frameX, frameY, frameSize, frameSize);
      analyzeImageData(imageData);
      code = jsQR(imageData.data, frameSize, frameSize, {
        inversionAttempts: 'dontInvert'
      });
    }

    if (code) {
      const debugInfo = {
        canvasSize: `${canvas.width}x${canvas.height}`,
        framePosition: `(${frameX.toFixed(0)}, ${frameY.toFixed(0)})`,
        frameSize: frameSize,
        videoReadyState: video.readyState,
        videoSize: `${video.videoWidth}x${video.videoHeight}`,
        timestamp: new Date().toLocaleTimeString(),
        usedFullScreen
      };
      addLog(`✅ 识别到二维码：${code.data}`, 'success');
      lastScanResult.value = JSON.stringify({
        content: code.data,
        debug: debugInfo
      }, null, 2);
      stopScan();
      showToast('扫码成功');
      emit('scan-success', code.data);
      scanFailCount = 0;
    } else {
      scanFailCount++;
    }
  } catch (drawErr: any) {
    addLog('绘制/识别失败：' + drawErr.message, 'error');
  }
}
const analyzeImageData = (imageData: ImageData) => {
  const data = imageData.data
  let min = 255, max = 0, sum = 0
  let darkPixels = 0, lightPixels = 0
  const sampleStep = 50
  
  // 边缘检测采样
  const edgeSamples = []
  
  for (let i = 0; i < data.length; i += 4 * sampleStep) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b)
    
    min = Math.min(min, gray)
    max = Math.max(max, gray)
    sum += gray
    
    if (gray < 128) {
      darkPixels++
    } else {
      lightPixels++
    }
    
    // 边缘检测采样
    if (i % (4 * sampleStep * 10) === 0) {
      edgeSamples.push(gray)
    }
  }
  
  // 计算边缘强度
  let edgeStrength = 0
  for (let i = 1; i < edgeSamples.length; i++) {
    edgeStrength += Math.abs(edgeSamples[i] - edgeSamples[i - 1])
  }
  edgeStrength = edgeStrength / edgeSamples.length
  
  const samples = Math.floor(data.length / (4 * sampleStep))
  const avg = sum / samples
  const contrast = max - min
  const darkRatio = (darkPixels / (darkPixels + lightPixels) * 100).toFixed(1)
  
  // 生成警告信息
  let warning = ''
  if (contrast < 30) {
    warning = '对比度过低，可能是光线不足或二维码模糊'
  } else if (contrast > 200) {
    warning = '对比度过高，可能有强光反射'
  } else if (avg < 50) {
    warning = '整体偏暗，需要增加光线'
  } else if (avg > 200) {
    warning = '整体偏亮，可能有强光直射'
  } else if (parseFloat(darkRatio) < 20 || parseFloat(darkRatio) > 80) {
    warning = '黑白像素比例异常，二维码可能曝光过度或不足'
  } else if (edgeStrength < 5) {
    warning = '边缘强度低，二维码可能模糊或失焦'
  }
  
  // 更新当前质量显示
  currentQuality.value = {
    range: `[${min},${max}]`,
    avg: avg.toFixed(0),
    contrast: contrast,
    darkRatio: darkRatio + '%',
    edgeStrength: edgeStrength.toFixed(1),
    warning: warning || undefined
  }
}
// 停止扫描
const stopScan = () => {
  addLog('停止扫码器');
  isScanning.value = false;
  // 清除定时器
  if (scanTimer) {
    window.clearTimeout(scanTimer);
    scanTimer = null;
  }
  // 停止摄像头流
  try {
    const stream = videoRef.value?.srcObject as MediaStream;
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
        addLog(`停止摄像头轨道：${track.kind}`, 'info');
      });
    }
  } catch (stopErr: any) {
    addLog('停止摄像头失败：' + stopErr.message, 'error');
  }
}

// 关闭扫码
const handleClose = () => {
  addLog('用户点击取消扫码')
  stopScan()
  emit('scan-close')
}

// 触发文件选择器
const triggerFileUpload = () => {
  fileInput.value?.click()
}

// 处理文件选择
const handleFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files || input.files.length === 0) {
    addLog('未选择任何文件', 'warn')
    return
  }

  const file = input.files[0]
  const reader = new FileReader()

  reader.onload = (e) => {
    const image = new Image()
    image.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        addLog('Canvas上下文创建失败', 'error')
        return
      }

      canvas.width = image.width
      canvas.height = image.height
      ctx.drawImage(image, 0, 0, image.width, image.height)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const code = jsQR(imageData.data, canvas.width, canvas.height, {
        inversionAttempts: 'dontInvert'
      })

      if (code) {
        addLog(`✅ 识别到二维码：${code.data}`, 'success')
        lastScanResult.value = JSON.stringify({
          content: code.data,
          debug: {
            canvasSize: `${canvas.width}x${canvas.height}`,
            framePosition: `(0, 0)`,
            frameSize: '',
            videoReadyState: '',
            videoSize: '',
            timestamp: new Date().toLocaleTimeString()
          }
        }, null, 2)
        stopScan()
        showToast('图片扫码成功')
        emit('scan-success', code.data)
      } else {
        addLog('未能识别二维码', 'warn')
        showToast('未能识别二维码')
      }
    }

    if (e.target?.result) {
      image.src = e.target.result as string
    }
  }

  reader.onerror = () => {
    addLog('读取文件失败', 'error')
    showToast('读取文件失败')
  }

  reader.readAsDataURL(file)
}
// 文件输入框引用
const fileInput = ref<HTMLInputElement | null>(null)

// 组件挂载
onMounted(() => {
  addLog('扫码组件挂载', 'info')
  initQRScanner()
})

// 组件卸载
onBeforeUnmount(() => {
  addLog('扫码组件卸载', 'info')
  stopScan()
})
</script>

<style scoped>
.scanner-container {
  position: relative;
  width: 100%;
  height: 100vh; /* 使用视口高度 */
  overflow: hidden; /* 防止滚动 */
  background: #000;
}
.scanner-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.scan-frame {
  width: 280px;
  height: 280px;
  border: 2px solid #07c160;
  position: relative;
  /* left: 50%;
  top: 40%;
  transform: translate(-50%, -50%); */
  margin-top: -20vh;
  box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.6);
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #07c160, transparent);
  animation: scan 1.5s linear infinite;
}

@keyframes scan {
  0% { top: 0; }
  100% { top: 100%; }
}

.hint-text {
  /* position: absolute;
  top: calc(40% + 160px); */
  width: 100%;
  text-align: center;
  color: #fff;
  font-size: 14px;
  z-index: 10;
  margin-top: 20px;
}
.control-buttons {
  position: absolute;
  bottom: 100px; /* 留出日志空间 */
  left: 0;
  width: 100%;
  padding: 0 20px;
  z-index: 10;
}

/* 日志面板样式 */
.log-panel {
  position: absolute;
  bottom: 20px;
  left: 0;
  width: 100%;
  padding: 0 10px;
  z-index: 20;
  color: #fff;
  font-size: 12px;
}

.log-title {
  background: rgba(0,0,0,0.7);
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.log-content {
  background: rgba(0,0,0,0.8);
  max-height: 200px;
  overflow-y: auto;
  margin-top: 5px;
  border-radius: 4px;
  padding: 5px;
}

.log-item {
  margin: 2px 0;
  padding: 2px 0;
  border-bottom: 1px solid #333;
}

.log-item.info {
  color: #fff;
}

.log-item.error {
  color: #ff4444;
}

.log-item.warn {
  color: #ffcc00;
}

.log-item.success {
  color: #00cc00;
}

.log-time {
  display: inline-block;
  width: 80px;
  color: #999;
}

.log-text {
  display: inline-block;
  word-break: break-all;
}
.result-label {
  font-size: 14px;
  color: #07c160;
  margin-bottom: 5px;
}

.result-content {
  font-size: 16px;
  word-break: break-all;
  margin-bottom: 10px;
}
.quality-display {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  padding: 12px;
  border-radius: 8px;
  z-index: 35;
  border: 1px solid #07c160;
  font-size: 13px;
}

.quality-title {
  color: #07c160;
  font-weight: bold;
  margin-bottom: 8px;
  text-align: center;
}

.quality-content {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.quality-item {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
}

.quality-warning {
  margin-top: 8px;
  padding: 6px;
  background: rgba(255, 193, 7, 0.2);
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #ffc107;
  font-size: 12px;
  text-align: center;
}

/* 调整 scan-result-display 的位置，避免重叠 */
.scan-result-display {
  top: 180px; /* 增加顶部间距，给质量显示留出空间 */
}
</style>